import type {
  RFQCriteria,
  RFQItem,
  RequestForQuotation,
  SourceOfSupply,
  VendorCriteriaValue,
} from '../types/purchase.types';
import { api, qs } from '../../../renderer/services/http';

function validateWeights(criteria: RFQCriteria[]): string | null {
  const topLevel = criteria.filter((c) => !c.parent_id && !c.is_group);
  const topSum = topLevel.reduce((s, c) => s + Number(c.weight || 0), 0);
  if (Math.abs(topSum - 100) > 0.01) {
    return `Top-level criteria weights must sum to 100% (currently ${topSum.toFixed(1)}%)`;
  }
  const parents = criteria.filter((c) => c.is_parent);
  for (const p of parents) {
    const children = criteria.filter((c) => c.parent_id === p.id);
    if (children.length > 0) {
      const childSum = children.reduce((s, c) => s + Number(c.weight || 0), 0);
      if (Math.abs(childSum - 100) > 0.01) {
        return `Sub-criteria under "${p.criteria_name}" must sum to 100% (currently ${childSum.toFixed(1)}%)`;
      }
    }
  }
  return null;
}

export const rfqService = {
  getAll: async (filters?: { status?: string }) =>
    api<RequestForQuotation[]>(`/api/purchase/rfqs${qs({ status: filters?.status })}`),

  getById: async (id: string) => api<RequestForQuotation>(`/api/purchase/rfqs/${id}`),

  create: async (input: any, status = 'draft') =>
    api('/api/purchase/rfqs', {
      method: 'POST',
      body: JSON.stringify({ ...input, status: status === 'submitted' ? 'open' : status }),
    }),

  saveItems: async (rfqId: string, items: RFQItem[]) =>
    api(`/api/purchase/rfqs/${rfqId}/items`, { method: 'PUT', body: JSON.stringify({ items }) }),

  saveCriteria: async (rfqId: string, criteria: RFQCriteria[]) => {
    const err = validateWeights(criteria);
    if (err) return { success: false as const, error: err };
    return api(`/api/purchase/rfqs/${rfqId}/criteria`, { method: 'PUT', body: JSON.stringify({ criteria }) });
  },

  validateCriteriaWeights: (criteria: RFQCriteria[]) => validateWeights(criteria),

  saveVendorCriteriaValues: async (rfqId: string, values: VendorCriteriaValue[]) =>
    api(`/api/purchase/rfqs/${rfqId}/values`, { method: 'PUT', body: JSON.stringify({ values }) }),

  addVendor: async (rfqId: string, vendorId: string) =>
    api(`/api/purchase/rfqs/${rfqId}/vendors`, { method: 'POST', body: JSON.stringify({ vendor_id: vendorId }) }),

  calculateScores: async (rfqId: string) =>
    api(`/api/purchase/rfqs/${rfqId}/scores`, { method: 'POST' }),

  selectVendorForItem: async (rfqId: string, rfqItemId: string, vendorId: string) =>
    api(`/api/purchase/rfqs/${rfqId}/select-item`, {
      method: 'POST',
      body: JSON.stringify({ rfq_item_id: rfqItemId, vendor_id: vendorId }),
    }),

  selectVendor: async (rfqId: string, vendorId: string) =>
    api(`/api/purchase/rfqs/${rfqId}/select-vendor`, {
      method: 'POST',
      body: JSON.stringify({ vendor_id: vendorId }),
    }),

  saveSourceOfSupply: async (rfqId: string, source: SourceOfSupply) =>
    api(`/api/purchase/rfqs/${rfqId}/source`, { method: 'PUT', body: JSON.stringify(source) }),

  generateRFQPR: async (rfqId: string) =>
    api(`/api/purchase/rfqs/${rfqId}/rfq-pr`, { method: 'POST' }),

  bypass: async (rfqId: string, reason: string) =>
    api(`/api/purchase/rfqs/${rfqId}/bypass`, { method: 'POST', body: JSON.stringify({ reason }) }),

  addQuotation: async (rfqId: string, quote: any) =>
    api(`/api/purchase/rfqs/${rfqId}/values`, { method: 'PUT', body: JSON.stringify({ values: quote?.lines || [] }) }),

  criteriaTemplates: [] as Array<{ name: string; criteria: RFQCriteria[] }>,

  saveCriteriaTemplate: async (templateName: string, criteria: RFQCriteria[]) =>
    api('/api/purchase/criteria-templates', {
      method: 'POST',
      body: JSON.stringify({ name: templateName, criteria }),
    }),

  getCriteriaTemplates: async () => api('/api/purchase/criteria-templates'),
};
